import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { getMe } from '../lib/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const sessionTimeoutRef = useRef(null);

  const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes

  const clearSessionTimer = useCallback(() => {
    if (sessionTimeoutRef.current) {
      clearTimeout(sessionTimeoutRef.current);
      sessionTimeoutRef.current = null;
    }
  }, []);

  const logout = useCallback(() => {
    clearSessionTimer();
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  }, [clearSessionTimer]);

  const resetSessionTimer = useCallback(() => {
    clearSessionTimer();
    sessionTimeoutRef.current = setTimeout(() => {
      logout();
      window.location.href = '/login?expired=true';
    }, INACTIVITY_TIMEOUT);
  }, [clearSessionTimer, logout, INACTIVITY_TIMEOUT]);

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const response = await getMe();
          setUser(response.data);
          localStorage.setItem('user', JSON.stringify(response.data));
        } catch (error) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, []);

  useEffect(() => {
    if (!user) return;
    
    resetSessionTimer();
    
    const handleActivity = () => {
      resetSessionTimer();
    };
    
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
    events.forEach(event => {
      document.addEventListener(event, handleActivity);
    });
    
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      clearSessionTimer();
    };
  }, [user, resetSessionTimer, clearSessionTimer]);

  const loginUser = useCallback((userData, token) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  }, []);

  const hasPermission = useCallback((requiredRoles) => {
    if (!user) return false;
    return requiredRoles.includes(user.rol);
  }, [user]);

  /**
   * Determina si el usuario puede acceder a un módulo específico.
   * - `administrador` y `direccion` acceden a todo.
   * - `administrativo` sólo si el módulo está en `modulos_permitidos`.
   * - Cualquier otro rol devuelve false por esta vía (usa hasPermission con
   *   una lista explícita de roles cuando corresponda).
   */
  const hasModuleAccess = useCallback((moduloKey) => {
    if (!user) return false;
    if (['administrador', 'direccion'].includes(user.rol)) return true;
    if (user.rol === 'administrativo') {
      return (user.modulos_permitidos || []).includes(moduloKey);
    }
    return false;
  }, [user]);

  const canAccessTurno = useCallback((turno) => {
    if (!user) return false;
    if (['administrador', 'direccion'].includes(user.rol)) return true;
    return !user.turno || user.turno === turno;
  }, [user]);

  const canAccessGrado = useCallback((grado) => {
    if (!user) return false;
    if (['administrador', 'direccion'].includes(user.rol)) return true;
    return !user.grados_asignados?.length || user.grados_asignados.includes(grado);
  }, [user]);

  const canAccessGrupo = useCallback((grupo) => {
    if (!user) return false;
    if (['administrador', 'direccion'].includes(user.rol)) return true;
    return !user.grupos_asignados?.length || user.grupos_asignados.includes(grupo);
  }, [user]);

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      loginUser,
      logout,
      hasPermission,
      hasModuleAccess,
      canAccessTurno,
      canAccessGrado,
      canAccessGrupo
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
